import React from 'react';

/**
 * Shared bits for the Party Engine admin tab. Everything here follows the handoff:
 * 2px black borders, zero radius, JetBrains Mono, UPPERCASE system voice, pastel only as
 * chip punctuation, hard black progress bars, a 1.2s step pulse as the only motion.
 */

export const PASTELS = ['#FFCCE6', '#A8D8EA', '#C6B8FF', '#FFF8EA'];

export const money = (cents) => {
  const c = typeof cents === 'number' ? cents : 0;
  const sign = c < 0 ? '-' : '';
  return `${sign}$${(Math.abs(c) / 100).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/** "2026-10-03T19:30" (already in the region's clock) → "SAT OCT 3, 7:30 PM". */
export const fmtLocal = (local) => {
  if (!local) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(local);
  if (!m) return local;
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]));
  const day = d.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });
  const mon = d.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' });
  return `${day} ${mon} ${d.getUTCDate()}, ${time}`.toUpperCase();
};

/** UTC ISO → viewer-local "TODAY, 9:41 AM" / "SEP 4, 9:41 AM". */
export const fmtWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (sameDay) return `TODAY, ${time}`.toUpperCase();
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${time}`.toUpperCase();
};

export const elapsed = (fromIso, toIso) => {
  if (!fromIso) return '00:00:00';
  const ms = Math.max(0, (toIso ? new Date(toIso) : new Date()) - new Date(fromIso));
  const s = Math.floor(ms / 1000);
  const hh = String(Math.floor(s / 3600)).padStart(2, '0');
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
};

const RUN_PILLS = {
  queued: { border: '#9CA3AF', bg: '#9CA3AF', color: '#fff', label: 'QUEUED' },
  researching: { border: '#000', bg: '#000', color: '#fff', label: 'RUNNING' },
  generating: { border: '#000', bg: '#000', color: '#fff', label: 'RUNNING' },
  rendering: { border: '#000', bg: '#000', color: '#fff', label: 'RUNNING' },
  publishing: { border: '#000', bg: '#000', color: '#fff', label: 'RUNNING' },
  awaiting_review: { border: '#000', bg: '#F9B82C', color: '#000', label: 'AWAITING REVIEW' },
  completed: { border: '#000', bg: '#15803D', color: '#fff', label: 'COMPLETED' },
  failed: { border: '#000', bg: '#DC2626', color: '#fff', label: 'FAILED' },
  canceled: { border: '#9CA3AF', bg: '#fff', color: '#6B7280', label: 'CANCELED' },
};

export function RunStatusPill({ status }) {
  const p = RUN_PILLS[status] || RUN_PILLS.queued;
  return (
    <span
      className="font-mono font-bold uppercase whitespace-nowrap"
      style={{ fontSize: 10, letterSpacing: '0.05em', padding: '2px 6px', border: `2px solid ${p.border}`, background: p.bg, color: p.color }}
    >
      {p.label}
    </span>
  );
}

const CONCEPT_PILLS = {
  draft: { bg: '#fff', color: '#000', label: 'DRAFT' },
  approved: { bg: '#15803D', color: '#fff', label: 'APPROVED' },
  rejected: { bg: '#fff', color: '#DC2626', label: 'REJECTED' },
  published: { bg: '#000', color: '#fff', label: 'PUBLISHED' },
  publish_failed: { bg: '#DC2626', color: '#fff', label: 'PUBLISH FAILED' },
  generating: { bg: '#000', color: '#fff', label: 'GENERATING…' },
};

export function ConceptStatusPill({ status }) {
  const p = CONCEPT_PILLS[status] || CONCEPT_PILLS.draft;
  return (
    <span
      className="rounded-full font-mono font-bold uppercase whitespace-nowrap"
      style={{ fontSize: 9, letterSpacing: '0.12em', padding: '5px 11px', background: p.bg, color: p.color }}
    >
      {p.label}
    </span>
  );
}

export function ModeChip({ mode, suffix = '' }) {
  const bg = mode === 'auto' ? '#A8D8EA' : '#C6B8FF';
  return (
    <span className="rounded-full font-mono font-bold uppercase whitespace-nowrap" style={{ fontSize: 9, letterSpacing: '0.12em', padding: '5px 11px', background: bg }}>
      {mode === 'auto' ? 'AUTO' : 'REVIEW'}{suffix}
    </span>
  );
}

export function CategoryChip({ label, index = 0 }) {
  return (
    <span className="rounded-full font-mono font-bold uppercase" style={{ fontSize: 9, padding: '5px 11px', background: PASTELS[index % PASTELS.length], letterSpacing: '0.12em' }}>
      {label}
    </span>
  );
}

/**
 * How we know the venue and its price:
 *   PRO LISTING     — a Pro venue business opted in and priced it.
 *   VERIFIED        — web search found the venue's private-hire page AND a posted rate.
 *   RATE ESTIMATED  — web search found the private-hire page; the rate is the model's estimate.
 * Every venue that reaches a card passed verification; the chip is about the number.
 * `href` (the evidence page) makes the chip a link.
 */
export function BasisChip({ basis, href }) {
  const map = {
    pro_listing: { border: '#15803D', color: '#000', label: 'PRO LISTING' },
    web_verified: { border: '#15803D', color: '#000', label: 'VERIFIED' },
    llm_estimate: { border: '#F9B82C', color: '#000', label: 'RATE ESTIMATED' },
    none: { border: '#9CA3AF', color: '#6B7280', label: 'NO VENUE' },
  };
  const p = map[basis] || map.none;
  const style = { fontSize: 9, padding: '5px 11px', borderRadius: 999, boxShadow: `inset 0 0 0 2px ${p.border}`, color: p.color, letterSpacing: '0.12em' };
  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="font-mono font-bold uppercase whitespace-nowrap hover:opacity-80" style={{ ...style, textDecoration: 'underline' }} title="Open the page that shows private hire">
        {p.label} ↗
      </a>
    );
  }
  return (
    <span className="font-mono font-bold uppercase whitespace-nowrap" style={style}>
      {p.label}
    </span>
  );
}

export function Chevron({ open }) {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter" aria-hidden="true">
      <path d={open ? 'M2 8 L6 4 L10 8' : 'M2 4 L6 8 L10 4'} />
    </svg>
  );
}

export function XIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter" aria-hidden="true">
      <path d="M2 2 L10 10 M10 2 L2 10" />
    </svg>
  );
}

export function Check() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter" aria-hidden="true">
      <path d="M2 6 L5 9 L10 3" />
    </svg>
  );
}

/** Hard black bar in a 2px track. `pulse` applies the step pulse to the fill. */
export function ProgressBar({ percent, height = 12, pulse = false, className = '' }) {
  return (
    <div className={`overflow-hidden rounded-full bg-[#F2F2F2] ${className}`} style={{ height }}>
      <div className={pulse ? 'pe-pulse' : ''} style={{ height: '100%', width: `${Math.max(0, Math.min(100, percent))}%`, background: '#000', borderRadius: 999 }} />
    </div>
  );
}

const BTN_BASE = 'inline-flex items-center justify-center rounded-full font-mono font-bold uppercase whitespace-nowrap transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2';

/** variant: primary (filled black) | secondary (white) | danger (red outline) | dangerFilled | text | dangerText */
export function Btn({ variant = 'secondary', size = 'md', className = '', children, ...rest }) {
  const sizes = {
    sm: { fontSize: 9, height: 32, padding: '0 14px', letterSpacing: '0.12em' },
    md: { fontSize: 10, height: 40, padding: '0 16px', letterSpacing: '0.12em' },
    lg: { fontSize: 11, height: 44, padding: '0 20px', letterSpacing: '0.14em' },
  };
  const variants = {
    primary: 'bg-black text-white hover:opacity-85',
    secondary: 'bg-[#F2F2F2] text-black hover:bg-[#E5E5E5]',
    danger: 'bg-[#FEE2E2] hover:bg-[#FECACA]',
    dangerFilled: 'text-white hover:opacity-85',
    text: 'bg-transparent text-black underline hover:opacity-80',
    dangerText: 'bg-transparent underline hover:opacity-80',
  };
  const style = { ...sizes[size] };
  if (variant === 'danger') { style.color = '#DC2626'; }
  if (variant === 'dangerFilled') { style.background = '#DC2626'; }
  if (variant === 'dangerText') { style.color = '#DC2626'; style.padding = 0; style.height = 'auto'; }
  if (variant === 'text') { style.padding = 0; style.height = 'auto'; }
  const hoverDanger = '';
  return (
    <button type="button" className={`${BTN_BASE} ${variants[variant]} ${hoverDanger} ${className}`} style={style} {...rest}>
      {children}
    </button>
  );
}

/** Modal shell: a white radius-28 card with the ring-gradient hairline along the top. */
export function Sheet({ width = 560, title, onClose, children, footer }) {
  return (
    <div className="fixed inset-0 safe-inset z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.4)' }} onClick={onClose}>
      <div
        className="relative w-full max-h-[90vh] overflow-y-auto rounded-[28px] bg-white font-mono"
        style={{ maxWidth: width }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {title && (
          <div className="flex items-center justify-between" style={{ padding: '22px 24px 8px' }}>
            <span className="font-bold uppercase" style={{ fontSize: 14, letterSpacing: '0.14em' }}>{title}</span>
            {onClose && (
              <button type="button" onClick={onClose} className="hover:opacity-80" aria-label="Close"><XIcon /></button>
            )}
          </div>
        )}
        {children}
        {footer && (
          <div className="flex justify-end gap-2 border-t border-[#F2F2F2]" style={{ padding: '16px 24px 22px' }}>{footer}</div>
        )}
      </div>
    </div>
  );
}

/** One-line consequence dialog. */
export function ConfirmSheet({ title, message, confirmLabel, cancelLabel = 'CANCEL', danger = false, busy = false, onConfirm, onCancel }) {
  return (
    <Sheet
      width={420}
      title={title}
      onClose={onCancel}
      footer={(
        <>
          <Btn variant="secondary" onClick={onCancel} disabled={busy}>{cancelLabel}</Btn>
          <Btn variant={danger ? 'dangerFilled' : 'primary'} onClick={onConfirm} disabled={busy}>{busy ? 'WORKING…' : confirmLabel}</Btn>
        </>
      )}
    >
      <p style={{ padding: 16, fontSize: 13, lineHeight: 1.6, textWrap: 'pretty' }}>{message}</p>
    </Sheet>
  );
}

/** Inject the one keyframe the design allows. Idempotent. */
export function PartyEngineStyles() {
  return (
    <style>{`
      @keyframes pe-pulse { 0%,100% { opacity: 1 } 50% { opacity: 0.2 } }
      .pe-pulse { animation: pe-pulse 1.2s steps(1, end) infinite; }
      .pe-hover-red-fill:hover:not(:disabled) { background: #DC2626 !important; color: #fff !important; }
      .pe-hover-red-text:hover:not(:disabled) { color: #DC2626 !important; }
      .pe-row:hover { background: #F5F5F5; }
      .pe-toggle:hover { background: #F5F5F5; }
      .pe-input { border: 2px solid #000; border-radius: 0; outline: none; font-family: inherit; font-size: 14px; padding: 9px 11px; width: 100%; box-sizing: border-box; background: #fff; }
      .pe-input:focus { box-shadow: 0 0 0 2px #fff, 0 0 0 4px #000; }
      .pe-input.pe-invalid { border-color: #DC2626; }
    `}</style>
  );
}
